import { api } from '@packages/backend/convex/_generated/api';
import {
  BRANDS,
  FEATURE_FLAGS,
  type FeatureFlagCvx,
  type FeatureFlagKey,
  getRestaurantPlanBrand,
  getRestaurantPlanChangeFeatureFlagKeys,
  getRestaurantPlanStaffFeatureFlagKeys,
  getUserErrorMessage,
  RESTAURANT_PLAN_KEYS,
  RESTAURANT_PLANS,
  type RestaurantPlanCvx,
  type RestaurantPlanKey,
  SMS_PACK,
} from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { ActionButton } from '@/components/ActionButton';
import { ErrorAlert } from '@/components/ErrorAlert';
import { FormWrapper } from '@/components/FormWrapper';
import { LoadingIcon } from '@/components/LoadingIcon';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { FormField } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { log } from '@/lib/logging';

/**
 * One switch per plan feature, each its own field: on a Convex sync, unsaved switches keep their
 * values while the rest follow Convex.
 */
type FormValues = Partial<Record<FeatureFlagKey, boolean>>;

export const Route = createFileRoute('/r/$restaurantId/s/features')({
  validateSearch: z.object({
    /**
     * Stripe Checkout's `success_url` for a subscription: the venue has paid, and the webhook saves
     * its plan (Tripleshot) or turns on its custom domain (Zest) soon.
     */
    subscribed: z.boolean().optional(),
    /** Stripe Checkout's `success_url` for an SMS pack: the webhook adds its SMS soon. */
    boughtSmsPack: z.boolean().optional(),
  }),
  component: FeaturesPage,
});

/**
 * Venue staff turn their plan's features on or off, Tripleshot staff pick and pay for their plan
 * through Stripe, and Zest staff pay for SMS packs and their custom domain. Super admins set every
 * other venue's plan. Other feature flags never show here.
 */
function FeaturesPage() {
  const { restaurantId } = Route.useParams();
  const { subscribed, boughtSmsPack } = Route.useSearch();
  const restaurantPlanQuery = useQuery(api.restaurantPlans.getByRestaurantId, { restaurantId });
  const featureFlagsQuery = useQuery(api.featureFlags.listByRestaurantId, { restaurantId });

  if (restaurantPlanQuery.isPending || featureFlagsQuery.isPending) return <LoadingIcon />;
  if (restaurantPlanQuery.isError || featureFlagsQuery.isError) return null; // Handled by parent component

  const restaurantPlan = restaurantPlanQuery.data;
  const brand = getRestaurantPlanBrand(restaurantPlan);
  const planKey = restaurantPlan?.planKey ?? null;
  // A saved plan change starts the plan picker and the features form again
  const restaurantPlanKey = `${planKey}-${restaurantPlan?.stripeSubscriptionId}`;

  return (
    <>
      {/* Only Tripleshot plans are billed through Stripe */}
      {brand === 'tripleshot' && (
        <PlanCard
          key={restaurantPlanKey}
          restaurantId={restaurantId}
          restaurantPlan={restaurantPlan}
          featureFlags={featureFlagsQuery.data}
          isBackFromCheckout={subscribed === true}
        />
      )}
      {brand === 'zest' && (
        <ZestBillingCards
          restaurantId={restaurantId}
          isBackFromSmsPackCheckout={boughtSmsPack === true}
          isBackFromCustomDomainCheckout={subscribed === true}
        />
      )}
      <Card>
        <CardHeader>
          <CardTitle>Features</CardTitle>
          <CardDescription>
            {planKey === null
              ? "You're not on a plan yet."
              : `Your plan: ${RESTAURANT_PLANS[planKey].label}`}
          </CardDescription>
        </CardHeader>
        {planKey !== null &&
          (getRestaurantPlanStaffFeatureFlagKeys(planKey).length === 0 ? (
            <CardContent>
              <p className="text-muted-foreground text-sm">
                Your plan has no features to turn on or off.
              </p>
            </CardContent>
          ) : (
            <FeaturesForm
              key={restaurantPlanKey}
              restaurantId={restaurantId}
              planKey={planKey}
              featureFlags={featureFlagsQuery.data}
            />
          ))}
      </Card>
    </>
  );
}

/**
 * The Tripleshot venue's plan, from Tripleshot's plans. It starts on the venue's plan, or on
 * Tripleshot's default plan without one. Without a subscription, staff pay in Stripe Checkout.
 * With one, they change their plan here, and manage their cards, invoices and cancelling in the
 * Stripe Customer Portal.
 */
const PlanCard = ({
  restaurantId,
  restaurantPlan,
  featureFlags,
  isBackFromCheckout,
}: {
  restaurantId: string;
  restaurantPlan: RestaurantPlanCvx | null;
  featureFlags: FeatureFlagCvx[];
  isBackFromCheckout: boolean;
}) => {
  const convex = useConvex();
  const isSubscribed = restaurantPlan !== null && restaurantPlan.stripeSubscriptionId !== null;
  const brand = getRestaurantPlanBrand(restaurantPlan);
  const brandPlanKeys = RESTAURANT_PLAN_KEYS.filter((key) => RESTAURANT_PLANS[key].brand === brand);
  const [selectedPlanKey, setSelectedPlanKey] = useState(
    restaurantPlan?.planKey ?? BRANDS[brand].defaultPlanKey,
  );
  const planKey = restaurantPlan?.planKey ?? null;

  const { turnedOnKeys, turnedOffKeys } = getRestaurantPlanChangeFeatureFlagKeys(
    planKey,
    selectedPlanKey,
  );
  // Only what actually changes: a flag already on doesn't turn on, one already off doesn't turn off
  const isEnabled = (key: FeatureFlagKey) =>
    featureFlags.some((featureFlag) => featureFlag.key === key);
  const turningOnLabels = turnedOnKeys
    .filter((key) => !isEnabled(key))
    .map((key) => FEATURE_FLAGS[key].label);
  const turningOffLabels = turnedOffKeys
    .filter((key) => isEnabled(key))
    .map((key) => FEATURE_FLAGS[key].label);

  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isChangingPlan, setIsChangingPlan] = useState(false);

  const subscribe = async () => {
    setIsRedirecting(true);
    try {
      window.location.href = await convex.action(api.billing.tripleshot.subscribe, {
        restaurantId,
        planKey: selectedPlanKey,
        returnUrl: getCurrentPageUrl(),
      });
    } catch (error) {
      toast.error("Couldn't open checkout", { description: getUserErrorMessage(error) });
      setIsRedirecting(false);
    }
  };

  const openBillingPortal = async () => {
    setIsRedirecting(true);
    try {
      window.location.href = await convex.action(api.billing.tripleshot.getBillingPortalUrl, {
        restaurantId,
        returnUrl: getCurrentPageUrl(),
      });
    } catch (error) {
      toast.error("Couldn't open billing", { description: getUserErrorMessage(error) });
      setIsRedirecting(false);
    }
  };

  const changePlan = async () => {
    setIsChangingPlan(true);
    try {
      await convex.action(api.billing.tripleshot.changePlan, {
        restaurantId,
        planKey: selectedPlanKey,
      });
    } catch (error) {
      toast.error("Couldn't change plan", { description: getUserErrorMessage(error) });
      setIsChangingPlan(false);
      return;
    }
    log.info('Plan changed', {
      area: 'restaurant_plans',
      brand,
      plan_key: selectedPlanKey,
      previous_plan_key: planKey,
    });
    setIsChangingPlan(false);
    setIsConfirmOpen(false);
    toast.success('Plan changed');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Plan</CardTitle>
        <CardDescription>
          {isSubscribed
            ? 'Changes are charged or credited on your next bill.'
            : isBackFromCheckout
              ? 'Thanks! Your plan will update in a moment.'
              : 'Pick your plan, then subscribe.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        <Select
          value={selectedPlanKey}
          onValueChange={(value) => {
            const planKey = brandPlanKeys.find((key) => key === value);
            if (planKey) setSelectedPlanKey(planKey);
          }}
        >
          <SelectTrigger aria-label="Plan" className="w-[280px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {brandPlanKeys.map((key) => (
              <SelectItem key={key} value={key}>
                {RESTAURANT_PLANS[key].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardContent>
      <CardFooter className="gap-2 border-t">
        {isSubscribed ? (
          <>
            <ActionButton
              disabled={selectedPlanKey === planKey}
              onClick={() => setIsConfirmOpen(true)}
            >
              Change plan
            </ActionButton>
            <ActionButton variant="outline" loading={isRedirecting} onClick={openBillingPortal}>
              Manage billing
            </ActionButton>
          </>
        ) : (
          // Back from Checkout: the subscription is paid, and the webhook saves it in a moment
          <ActionButton disabled={isBackFromCheckout} loading={isRedirecting} onClick={subscribe}>
            Subscribe
          </ActionButton>
        )}
      </CardFooter>
      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change your plan?</AlertDialogTitle>
            <AlertDialogDescription className="grid gap-2">
              {/* Spans, not paragraphs: the description renders as a <p> */}
              {turningOnLabels.length > 0 && <span>Turns on: {turningOnLabels.join(', ')}</span>}
              {turningOffLabels.length > 0 && <span>Turns off: {turningOffLabels.join(', ')}</span>}
              <span>We'll charge or credit the difference on your next bill.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <ActionButton loading={isChangingPlan} onClick={changePlan}>
              Yes
            </ActionButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

/**
 * What a Zest venue pays for through Zest Billing: SMS packs and its custom domain. Staff pay in
 * Stripe Checkout.
 */
const ZestBillingCards = ({
  restaurantId,
  isBackFromSmsPackCheckout,
  isBackFromCustomDomainCheckout,
}: {
  restaurantId: string;
  isBackFromSmsPackCheckout: boolean;
  isBackFromCustomDomainCheckout: boolean;
}) => {
  const zestBillingQuery = useQuery(api.billing.zest.getByRestaurantId, { restaurantId });

  if (zestBillingQuery.isPending) return <LoadingIcon />;
  if (zestBillingQuery.isError) {
    return <ErrorAlert title="Error loading billing" message={zestBillingQuery.error.message} />;
  }

  const { smsCredit, customDomainSubscription } = zestBillingQuery.data;
  return (
    <>
      <SmsCreditsCard
        restaurantId={restaurantId}
        // No row: the venue never bought a pack
        balance={smsCredit?.balance ?? 0}
        isBackFromCheckout={isBackFromSmsPackCheckout}
      />
      <CustomDomainCard
        restaurantId={restaurantId}
        isSubscribed={customDomainSubscription !== null}
        isBackFromCheckout={isBackFromCustomDomainCheckout}
      />
    </>
  );
};

/**
 * The orders the Zest venue has customer SMS left for: each online order uses one. Staff buy more
 * in Stripe Checkout.
 */
const SmsCreditsCard = ({
  restaurantId,
  balance,
  isBackFromCheckout,
}: {
  restaurantId: string;
  balance: number;
  isBackFromCheckout: boolean;
}) => {
  const convex = useConvex();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const buySmsPack = async () => {
    setIsRedirecting(true);
    try {
      window.location.href = await convex.action(api.billing.zest.buySmsPack, {
        restaurantId,
        returnUrl: getCurrentPageUrl(),
      });
    } catch (error) {
      toast.error("Couldn't open checkout", { description: getUserErrorMessage(error) });
      setIsRedirecting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>SMS</CardTitle>
        <CardDescription>
          {isBackFromCheckout
            ? 'Thanks! Your SMS will be added in a moment.'
            : 'Customers get an SMS when you accept or cancel their online order.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-1">
        <p className="font-medium">
          SMS left for {balance} {balance === 1 ? 'order' : 'orders'}
        </p>
        {balance === 0 && (
          <p className="text-muted-foreground text-sm">
            Customers won't get an SMS until you buy more.
          </p>
        )}
      </CardContent>
      <CardFooter className="border-t">
        <ActionButton loading={isRedirecting} onClick={buySmsPack}>
          Buy SMS for {SMS_PACK.orderCount} orders
        </ActionButton>
      </CardFooter>
    </Card>
  );
};

/**
 * The Zest venue's custom domain. Without a subscription, staff pay in Stripe Checkout. With one,
 * OrderHub sets the domain up with them, and they manage their cards, invoices and cancelling in
 * the Stripe Customer Portal.
 */
const CustomDomainCard = ({
  restaurantId,
  isSubscribed,
  isBackFromCheckout,
}: {
  restaurantId: string;
  isSubscribed: boolean;
  isBackFromCheckout: boolean;
}) => {
  const convex = useConvex();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const subscribe = async () => {
    setIsRedirecting(true);
    try {
      window.location.href = await convex.action(api.billing.zest.subscribeCustomDomain, {
        restaurantId,
        returnUrl: getCurrentPageUrl(),
      });
    } catch (error) {
      toast.error("Couldn't open checkout", { description: getUserErrorMessage(error) });
      setIsRedirecting(false);
    }
  };

  const openBillingPortal = async () => {
    setIsRedirecting(true);
    try {
      window.location.href = await convex.action(api.billing.zest.getBillingPortalUrl, {
        restaurantId,
        returnUrl: getCurrentPageUrl(),
      });
    } catch (error) {
      toast.error("Couldn't open billing", { description: getUserErrorMessage(error) });
      setIsRedirecting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Custom domain</CardTitle>
        <CardDescription>
          {isSubscribed
            ? "You're subscribed. We'll be in touch to set up your domain."
            : isBackFromCheckout
              ? 'Thanks! Your custom domain will turn on in a moment.'
              : 'Take online orders on your own website address, like order.yourcafe.com.'}
        </CardDescription>
      </CardHeader>
      <CardFooter className="border-t">
        {isSubscribed ? (
          <ActionButton variant="outline" loading={isRedirecting} onClick={openBillingPortal}>
            Manage billing
          </ActionButton>
        ) : (
          // Back from Checkout: the subscription is paid, and the webhook saves it in a moment
          <ActionButton disabled={isBackFromCheckout} loading={isRedirecting} onClick={subscribe}>
            Subscribe
          </ActionButton>
        )}
      </CardFooter>
    </Card>
  );
};

/** One switch per plan feature, saved together. */
const FeaturesForm = ({
  restaurantId,
  planKey,
  featureFlags,
}: {
  restaurantId: string;
  planKey: RestaurantPlanKey;
  featureFlags: FeatureFlagCvx[];
}) => {
  const convex = useConvex();
  const planFeatureFlagKeys = getRestaurantPlanStaffFeatureFlagKeys(planKey);
  // Each plan feature's switch: on when the venue has its row
  const formValues: FormValues = Object.fromEntries(
    planFeatureFlagKeys.map((key) => [
      key,
      featureFlags.some((featureFlag) => featureFlag.key === key),
    ]),
  );
  const form = useForm<FormValues>({ defaultValues: formValues });

  // Subscribe to changes
  useEffect(() => {
    // Ensure un-saved user changes are kept on Convex sync
    form.reset(formValues, { keepDirty: true, keepDirtyValues: true });
  }, [featureFlags]);

  const onSubmit = async (values: FormValues) => {
    // Only the switches that differ from Convex: saving the others could undo a change made
    // elsewhere since. Hoisted out of the `try`: React Compiler can't lower a conditional inside one.
    const changedKeys = planFeatureFlagKeys.filter(
      (key) => values[key] !== form.formState.defaultValues?.[key],
    );
    const enabledKeys = changedKeys.filter((key) => values[key] === true);
    try {
      await convex.mutation(api.restaurantPlans.saveFeatureFlags, {
        restaurantId,
        keys: changedKeys,
        enabledKeys,
      });
    } catch (error) {
      toast.error("Couldn't save", { description: getUserErrorMessage(error) });
      return;
    }
    for (const key of changedKeys) {
      log.info('Feature flag changed', {
        area: 'feature_flags',
        feature_flag_key: key,
        is_enabled: enabledKeys.includes(key),
      });
    }
    // The saved values are the new baseline, so nothing is dirty any more
    form.reset(form.getValues());
    toast.success('Saved successfully');
  };

  return (
    <>
      <CardContent>
        <FormWrapper {...form}>
          <form
            id="form"
            aria-label="Features"
            onSubmit={form.handleSubmit(onSubmit)}
            className="grid gap-3"
          >
            <p className="text-muted-foreground text-sm">Changes reach every iPad straight away.</p>
            {planFeatureFlagKeys.map((key) => (
              <div
                key={key}
                className="flex items-center justify-between gap-4 rounded-lg border p-4"
              >
                <label className="grid gap-1" htmlFor={`feature-${key}`}>
                  <span className="font-medium">{FEATURE_FLAGS[key].label}</span>
                  <span className="text-muted-foreground text-sm">
                    {FEATURE_FLAGS[key].description}
                  </span>
                </label>
                <FormField
                  control={form.control}
                  name={key}
                  render={({ field }) => (
                    <Switch
                      id={`feature-${key}`}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
            ))}
          </form>
        </FormWrapper>
      </CardContent>
      <CardFooter className="border-t">
        <ActionButton
          disabled={form.formState.isSubmitting || !form.formState.isDirty}
          loading={form.formState.isSubmitting}
          type="submit"
          form="form"
        >
          Save
        </ActionButton>
      </CardFooter>
    </>
  );
};

/** The current page's URL without its query, for Stripe to send staff back to. */
const getCurrentPageUrl = (): string =>
  `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
